import React, {memo, FC, useState, useMemo} from "react";
import {FormikProps} from "formik";
import {Alert, Radio} from "antd";
import {CheckCircleFilled, ExclamationCircleFilled} from "@ant-design/icons";
import {blue, green, orange, yellow} from "@ant-design/colors";
import css from "./LessonBookings.less";
import {PossibleFormikPropsType} from "../CourseCreate/CourseCreateForm";
import {GetBookingsByCourseIdWithCourseLessonBookingsQuery, GetCourseByIdQuery, PaymentInterval, PaymentType, TablesEnum, UpdateCourseAndBookingsInput} from "../../../generated/graphql";
import useBookingSelector, {BookingSelector} from "./useBookingSelector";
import CurrentBooking from "./CurrentBooking";
import PreviewBooking, {PreviewBookingType} from "./PreviewBooking";
import {useHolidays, getChangedPaymentIds, ScheduledPaymentType} from "./helper";
import BookingDetails from "./BookingDetails/BookingDetails";
import {useLocalStorageOptions} from "../../../helpers/columnsLocalStorageHandler";
import ModifyBookingsDocumentation from "./ModifyBookingsDocumentation/ModifyBookingsDocumentation";
import {isBookingSettled} from "../../../helpers/bookingDetailsRouting";
import Button from "../../../views/Button/Button";
import useHighestRole from "../../../helpers/useHighestRole";
import Roles from "../../../../shared/models/roles";

type ModalContainerProps = {
    courseId: string;
    formikProps: FormikProps<PossibleFormikPropsType>;
    bookingsOfCourse?: GetBookingsByCourseIdWithCourseLessonBookingsQuery["bookingsByCourseIdWithCourseLessonBookings"];
    hideSelector?: boolean;
};

type LessonsOfTheCourseType = GetCourseByIdQuery["courseById"]["lessons"];

export const defaultPageSize = 10;

const ModalContainer: FC<ModalContainerProps> = ({courseId, formikProps, bookingsOfCourse, hideSelector}) => {
    const [previewCurrentBooking, setPreviewCurrentBooking] = useState<PreviewBookingType | undefined>(undefined);
    const [openDocumentation, setOpenDocumentation] = useState(false);
    // Vorschau-Schalter „Heute / Neu“: zeigt Superusern das neue Preismodell, ohne zu schreiben.
    const [plannerMode, setPlannerMode] = useState(false);
    const isSuperUser = useHighestRole() === Roles.Superuser;
    const tablesEnum: TablesEnum = TablesEnum.LessonBookings;
    const [options, setOptions] = useLocalStorageOptions({
        localStorageName: tablesEnum,
        defaultOptions: {},
        totalResults: 0,
    });
    const {values} = formikProps;
    const {selectedBooking, allBookings, setSelectedBooking} = useBookingSelector(bookingsOfCourse);
    const lessonsOfTheCourse: LessonsOfTheCourseType = values.lessons as LessonsOfTheCourseType;

    const {holidays, loadingHolidays, getHolidayInfo} = useHolidays(lessonsOfTheCourse[0]?.startDateTime, lessonsOfTheCourse[lessonsOfTheCourse.length - 1]?.endDateTime);

    // Berechne geänderte Zahlungsraten (muss vor early returns sein)
    const originalPayments = selectedBooking?.booking?.scheduledPayments ?? [];
    const previewPayments = previewCurrentBooking?.scheduledPayments ?? [];
    const changedPaymentIds = useMemo(() => {
        if (!previewCurrentBooking || !selectedBooking?.booking) {
            return {changedIds: new Set<string>(), addedIds: new Set<string>(), removedIds: new Set<string>()};
        }

        return getChangedPaymentIds(originalPayments as Array<ScheduledPaymentType>, previewPayments as Array<ScheduledPaymentType>);
    }, [originalPayments, previewPayments, previewCurrentBooking, selectedBooking]);

    // Prüfe ob Gesamtpreis sich geändert hat
    const originalGrossPrice = selectedBooking?.booking?.grossPrice;
    const previewGrossPrice = previewCurrentBooking?.grossPrice;
    const isTotalPriceChanged = useMemo(() => {
        if (!previewCurrentBooking || !selectedBooking?.booking) {
            return false;
        }

        const originalPrice = originalGrossPrice ?? 0;
        const previewPrice = previewGrossPrice ?? 0;

        return Math.abs(previewPrice - originalPrice) > 0.01;
    }, [originalGrossPrice, previewGrossPrice, previewCurrentBooking, selectedBooking]);

    if (!loadingHolidays && !bookingsOfCourse?.length) {
        return (
            <div>
                <div>{"Dieser Kurs hat keine Buchungen"}</div>
            </div>
        );
    }

    if (!selectedBooking || !selectedBooking.booking) {
        return (
            <div>
                <div>{"Keine Buchung ausgewählt"}</div>
            </div>
        );
    }

    const {paymentType} = selectedBooking.booking;
    const paymentInterval = values.paymentInterval;

    const BookingInfo = () => {
        if (!previewCurrentBooking) {
            return null;
        }

        const bookingIsCanceled = previewCurrentBooking.canceled;
        const bookingIsSettled = isBookingSettled(previewCurrentBooking);
        const knowledgeOfBookerText = bookingIsSettled
            ? "Da diese Buchung bereits abgerechnet wurde, kann es sein, dass der Bucher bereits eine Rechnung erhalten hat.\nGegebenefalls muss er über diese Änderungen informiert werden."
            : "Der Bucher hat wahrscheinlich bereits eine Buchungsbestätigung mit dem Preis, den Kursterminen und den Zahlterminen bekommen.\nGegebenefalls muss er über die Änderungen informiert werden.";

        const getBookingInfo = () => {
            const coursePricePerLesson: boolean = paymentInterval === PaymentInterval.PerLesson;
            const monthlyPayment: boolean = paymentType === PaymentType.Monthly;
            const hasPaidScheduledPayments: boolean = previewCurrentBooking.scheduledPayments?.some((scheduledPayment) => scheduledPayment.paymentDone !== null) ?? false;
            const textArray = [];
            const bookingPriceInfo = coursePricePerLesson
                ? "Da der Preis dieser Buchung pro Kurseinheit berechnet wird, kann sich der Gesamtpreis der Buchung ändern."
                : "Da der Preis dieser Buchung für den ganzen Kurs berechnet wird,/nändert sich der Gesamtpreis der Buchung nicht.";
            const monthlyPaymentInfo = monthlyPayment
                ? "Da diese Buchung monatliche Raten hat, können sich die Raten ändern."
                : "Die Fälligkeit der Bezahlung kann sich durch das Ändern der Kurseinheiten ändern.";
            const paidScheduledPaymentsInfo = "Diese Buchung wurde bereits teilweise bezahlt. Bezahlte Posten werden nicht mehr geändert.";

            textArray.push(bookingPriceInfo);
            textArray.push(monthlyPaymentInfo);
            if (hasPaidScheduledPayments) {
                textArray.push(paidScheduledPaymentsInfo);
            }

            return (
                <div className={css.canceledBookingText}>
                    <ul>
                        {textArray.map((text, index) => (
                            <li key={index}>{text}</li>
                        ))}
                    </ul>
                </div>
            );
        };

        if (bookingIsCanceled) {
            const message = (
                <div>
                    <div className={css.canceledBookingTitle}>{"DieseBuchung ist storniert"}</div>
                    <div className={css.canceledBookingText}>{"Stornierte Buchungen können nicht mehr bearbeitet werden."}</div>
                </div>
            );

            return <Alert className={css.alertCard} message={message} type="error" />;
        }

        if (bookingIsSettled) {
            const message = (
                <div className={css.alertCardContent}>
                    <div className={css.canceledBookingTitle}>
                        {"Diese Buchung ist bereits abgerechnet!"}
                        <ExclamationCircleFilled style={{color: orange[4]}} />
                    </div>
                    <div className={css.canceledBookingText}>{getBookingInfo()}</div>
                    <div className={css.bookerHintText} style={{backgroundColor: yellow[2]}}>
                        {knowledgeOfBookerText}
                    </div>
                </div>
            );

            return <Alert className={css.alertCard} message={message} type="warning" />;
        }

        const message = (
            <div>
                <div className={css.canceledBookingTitle}>
                    {"Diese Buchung ist noch nicht abgerechnet"}
                    <CheckCircleFilled style={{color: green[3]}} />
                    {/* <InfoCircleOutlined style={{color: blue[3], position: "absolute", right: 0, top: 0}} /> */}
                    <Button type="link" onClick={() => setOpenDocumentation(true)} style={{color: blue[3], position: "absolute", right: 0, top: 4}}>
                        {"mehr Info"}
                    </Button>
                </div>
                <div className={css.canceledBookingText}>{getBookingInfo()}</div>
                <div className={css.bookerHintText} style={{backgroundColor: green[1]}}>
                    {knowledgeOfBookerText}
                </div>
            </div>
        );

        return <Alert className={css.alertCard} message={message} type="info" />;
    };

    return (
        <>
            {!hideSelector && <BookingSelector selectedBooking={selectedBooking} allBookings={allBookings} setSelectedBooking={setSelectedBooking} />}

            <div className={css.modalContainer}>
                <ModifyBookingsDocumentation asModal={true} open={openDocumentation} onClose={() => setOpenDocumentation(false)} />
                <BookingInfo />
                <BookingDetails booking={selectedBooking.booking} />
                <div className={css.bookingContainer}>
                    <div className={css.modalContainerLeft}>
                        <div className={css.header}>{"Bisherige Buchung"}</div>
                        <CurrentBooking
                            booking={selectedBooking.booking}
                            getHolidayInfo={getHolidayInfo}
                            formikProps={formikProps as FormikProps<UpdateCourseAndBookingsInput>}
                            options={options}
                            setOptions={setOptions}
                            changedPaymentIds={isSuperUser && plannerMode ? undefined : changedPaymentIds}
                            isTotalPriceChanged={isSuperUser && plannerMode ? false : isTotalPriceChanged}
                        />
                    </div>
                    <div className={css.modalContainerRight}>
                        <div className={css.header}>{"Vorschau der geänderten Buchung"}</div>
                        {isSuperUser && (
                            <div style={{display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, padding: "4px 8px"}}>
                                <span style={{fontSize: "0.85em", color: "#888"}}>{"Berechnung (Superuser)"}</span>
                                <Radio.Group size="small" value={plannerMode ? "neu" : "heute"} onChange={(event) => setPlannerMode(event.target.value === "neu")}>
                                    <Radio.Button value="heute">{"Heute"}</Radio.Button>
                                    <Radio.Button value="neu">{"Neu (Entwurf)"}</Radio.Button>
                                </Radio.Group>
                            </div>
                        )}
                        <PreviewBooking
                            plannerMode={isSuperUser && plannerMode}
                            previewCurrentBooking={previewCurrentBooking}
                            setPreviewCurrentBooking={setPreviewCurrentBooking}
                            booking={selectedBooking.booking}
                            getHolidayInfo={getHolidayInfo}
                            formikProps={formikProps as FormikProps<UpdateCourseAndBookingsInput>}
                            options={options}
                            setOptions={setOptions}
                            holidays={holidays}
                            changedPaymentIds={changedPaymentIds}
                            isTotalPriceChanged={isTotalPriceChanged}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default memo(ModalContainer);
